/**
 * Helpers and primitives shared by more than one listing-detail module (moved verbatim from
 * ListingDetailPage.tsx): rent/date formatting, the jurisdiction warning banner, the lifecycle
 * confirm dialog, and the jurisdiction-constraints query both listing editors read.
 */

import { useQuery } from '@tanstack/react-query';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  fetchJurisdictionConstraints,
  type ConstraintWarning,
  type Listing,
} from '../../../lib/leasing.api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Rent / phone / date formatters: the one implementation the shared listing view also uses. */
export { formatPhoneDisplay, formatRentCents, formatDate } from '@resida/listing-view';

// ---------------------------------------------------------------------------
// Warning Banner
// ---------------------------------------------------------------------------

interface WarningBannerProps {
  warnings: ConstraintWarning[];
}

export function WarningBanner({ warnings }: WarningBannerProps) {
  if (warnings.length === 0) return null;
  return (
    <div
      data-testid="listing-warning-banner"
      className="mb-4 p-3 bg-warning/10 border border-warning/25 rounded-md text-warning"
      role="alert"
    >
      <p className="text-sm font-medium mb-1">
        ⚠ {warnings.length} jurisdiction warning{warnings.length > 1 ? 's' : ''} — review before publishing.
      </p>
      <ul className="list-disc list-inside text-xs space-y-0.5">
        {warnings.map((w) => (
          <li key={w.code} data-testid={`listing-warning-item-${w.code}`}>
            {w.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Lifecycle Confirmation Dialogs
//
// Every lifecycle button (Pause/Reopen/Close) kicks off a business process with
// side effects the landlord doesn't otherwise see (intake email behavior, public
// visibility, listing-site sync) — so each one confirms first with plain-language
// copy on what the system is about to do, built on the same shared Dialog primitive
// used elsewhere on this page (ShareListingButton) for consistent focus-trap/Escape/
// click-outside a11y. Publishing / renewing is a paid flow with its own dialogs
// (ListingPublishDialog / RenewListingDialog).
// ---------------------------------------------------------------------------

interface LifecycleConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  onConfirm: () => void;
  isSubmitting: boolean;
  destructive?: boolean;
  testIdPrefix: string;
  children?: React.ReactNode;
}

export function LifecycleConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pendingLabel,
  onConfirm,
  isSubmitting,
  destructive,
  testIdPrefix,
  children,
}: LifecycleConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!isSubmitting) onOpenChange(next); }}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {children}

        <div className="flex gap-3">
          <Button
            type="button"
            data-testid={`${testIdPrefix}-confirm`}
            onClick={onConfirm}
            disabled={isSubmitting}
            className={cn(
              'flex-1 min-h-[44px]',
              destructive && 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
            )}
          >
            {isSubmitting ? pendingLabel : confirmLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid={`${testIdPrefix}-cancel`}
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[44px]"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Jurisdiction constraints — one query (one key) for the listing-fields and screening editors
// ---------------------------------------------------------------------------

/**
 * A unit's jurisdiction constraints (soft warnings / help text only, BR-1a). Keyed by unit, so the
 * guide (before a Listing exists) and the live editors share one cache entry.
 */
export function useUnitConstraints(unitId: string | null | undefined, enabled = true) {
  const query = useQuery({
    queryKey: ['listing-constraints', unitId],
    queryFn: () => fetchJurisdictionConstraints(unitId!),
    enabled: enabled && !!unitId,
  });
  return {
    constraints: query.data?.constraints ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** The listing's unit constraints; not fetched for a closed listing. */
export function useListingConstraints(listing: Listing) {
  return useUnitConstraints(listing.unitId, listing.status !== 'closed').constraints;
}
